import { useEffect, useId, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Button } from './Button';

/**
 * Modal and Drawer are built on the native <dialog> element, which gives us a real
 * focus trap, inert background, Escape handling and top-layer stacking for free.
 * Content mounts only while open, so every open starts from a clean state (no stale
 * form values from the last time).
 */

let openCount = 0;

function lockScroll(lock: boolean): void {
  openCount = Math.max(0, openCount + (lock ? 1 : -1));
  document.documentElement.style.overflow = openCount > 0 ? 'hidden' : '';
}

interface DialogShellProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: ReactNode;
  children: ReactNode;
  /** Buttons pinned to the bottom. */
  footer?: ReactNode;
  /** When false, Escape and backdrop clicks do nothing (use while a save is in flight). */
  dismissible?: boolean;
  className?: string;
  panelClassName?: string;
}

function DialogShell({ open, onClose, title, description, children, footer, dismissible = true, className, panelClassName }: DialogShellProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const descriptionId = useId();

  const latest = useRef({ onClose, dismissible });
  useEffect(() => {
    latest.current = { onClose, dismissible };
  });

  // Escape (the native "cancel" event) and presses on the backdrop route through onClose,
  // unless a save is in flight (dismissible=false).
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) {
      return;
    }
    const onCancel = (event: Event): void => {
      event.preventDefault();
      if (latest.current.dismissible) {
        latest.current.onClose();
      }
    };
    // A press on the backdrop targets the <dialog> itself; the panel fills the rest.
    const onPointerDown = (event: MouseEvent): void => {
      if (latest.current.dismissible && event.target === dialog) {
        latest.current.onClose();
      }
    };
    dialog.addEventListener('cancel', onCancel);
    dialog.addEventListener('mousedown', onPointerDown);
    return () => {
      dialog.removeEventListener('cancel', onCancel);
      dialog.removeEventListener('mousedown', onPointerDown);
    };
  }, []);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog || !open) {
      return;
    }
    dialog.showModal();
    lockScroll(true);
    // Runs when `open` flips to false and on unmount: always releases the scroll lock.
    return () => {
      lockScroll(false);
      if (dialog.open) {
        dialog.close();
      }
    };
  }, [open]);

  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
      className={cn('bg-transparent backdrop:bg-slate-900/45', className)}
    >
      {open && (
        <div className={cn('relative flex max-h-full flex-col overflow-hidden bg-white shadow-pop', panelClassName)}>
          <div className="border-b border-slate-100 px-6 py-4 pr-14">
            <h2 id={titleId} className="font-display text-lg font-bold text-slate-900">
              {title}
            </h2>
            {description && (
              <p id={descriptionId} className="mt-1 text-sm text-slate-500">
                {description}
              </p>
            )}
          </div>
          <div className="scroll-thin min-h-0 flex-1 overflow-y-auto px-6 py-5">{children}</div>
          {footer && <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-100 bg-slate-50/60 px-6 py-3">{footer}</div>}
          {/* Last in DOM order so the first form control, not Close, receives initial focus. */}
          <button
            type="button"
            aria-label="Close"
            disabled={!dismissible}
            onClick={onClose}
            className="absolute right-3 top-3 inline-flex size-9 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900 disabled:opacity-40"
          >
            <X className="size-5" aria-hidden />
          </button>
        </div>
      )}
    </dialog>
  );
}

const modalSizes = { sm: 'max-w-md', md: 'max-w-xl', lg: 'max-w-3xl', xl: 'max-w-5xl' } as const;

export interface ModalProps extends Omit<DialogShellProps, 'className' | 'panelClassName'> {
  size?: keyof typeof modalSizes;
}

/** Centered dialog for short, focused tasks (collect a payment, confirm, a small form). */
export function Modal({ size = 'md', ...props }: ModalProps) {
  return (
    <DialogShell
      {...props}
      className={cn('m-auto w-[calc(100%-1.5rem)] animate-scale-in p-0', modalSizes[size], 'max-h-[calc(100dvh-2rem)] overflow-visible')}
      panelClassName="rounded-xl max-h-[calc(100dvh-2rem)]"
    />
  );
}

const drawerSizes = { md: 'sm:max-w-lg', lg: 'sm:max-w-2xl', xl: 'sm:max-w-4xl' } as const;

export interface DrawerProps extends Omit<DialogShellProps, 'className' | 'panelClassName'> {
  size?: keyof typeof drawerSizes;
}

/** Right-hand panel for the detail of a row (student, due, issue). Replaces modal-on-modal. */
export function Drawer({ size = 'lg', ...props }: DrawerProps) {
  return (
    <DialogShell
      {...props}
      className={cn('m-0 ml-auto h-dvh max-h-none w-full max-w-none animate-slide-in-right overflow-visible p-0', drawerSizes[size])}
      panelClassName="h-dvh rounded-none sm:rounded-l-xl"
    />
  );
}

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  tone?: 'primary' | 'danger';
  /** True while the action runs: blocks double-submit and dismissal. */
  loading?: boolean;
}

/** "Are you sure" for destructive or irreversible actions. State the consequence in `description`. */
export function ConfirmDialog({ open, onClose, onConfirm, title, description, confirmLabel, tone = 'primary', loading = false }: ConfirmDialogProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      dismissible={!loading}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant={tone === 'danger' ? 'danger' : 'primary'} loading={loading} onClick={() => void onConfirm()}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p className="text-sm leading-relaxed text-slate-600">{description}</p>
    </Modal>
  );
}
